import { BadRequestError } from "@/wab/shared/ApiErrors/errors";
import type { CmsFieldMeta } from "@/wab/shared/ApiSchema";
import {
  ASSISTANT_CMS_DRAFT_BOUNDS,
  cmsDraftFieldValueBounded,
  cmsDraftValueMatches,
  cmsNestedFieldSupported,
} from "@/wab/shared/copilot/assistant-cms-draft";
import { assistantJsonSchema } from "@/wab/shared/copilot/assistant-protocol";
import { z } from "zod";

/**
 * Path-level edit of one value nested inside a list/object field, in one
 * locale. The native editor localizes only root fields, so a "nested localized
 * edit" is an edit inside a localized root list/object: the locale selects the
 * root value, the path walks the schema below it. Pure: the server operation
 * supplies the current draft base and verifies the persisted readback.
 *
 * Path segments: a list takes an item index (0..length; `length` appends), an
 * item or object takes a child field identifier. `[]` addresses the root field
 * value itself.
 */
const DANGEROUS_KEYS = new Set(["__proto__", "constructor", "prototype"]);
const safeKey = z
  .string()
  .min(1)
  .max(500)
  .refine((k) => !DANGEROUS_KEYS.has(k), "Dangerous dictionary key");
export const CMS_ROW_PATCH_BOUNDS = { edits: 50 } as const;

export const cmsRowPatchEditSchema = z
  .object({
    locale: z.string().max(500).refine((k) => !DANGEROUS_KEYS.has(k)),
    field: safeKey,
    path: z
      .array(z.union([safeKey, z.number().int().min(0).max(100_000)]))
      .max(ASSISTANT_CMS_DRAFT_BOUNDS.nestedDepth * 2)
      .optional(),
    /** Replacement for the addressed position (null clears an optional one). */
    value: assistantJsonSchema.optional(),
    /** Removes a list item / object key; at the root it clears the field to null. */
    remove: z.literal(true).optional(),
  })
  .strict()
  .refine(
    (e) => Object.prototype.hasOwnProperty.call(e, "value") !== (e.remove === true),
    "An edit carries exactly one of value or remove",
  );
export type CmsRowPatchEdit = z.infer<typeof cmsRowPatchEditSchema>;
export const cmsRowPatchEditsSchema = z
  .array(cmsRowPatchEditSchema)
  .min(1)
  .max(CMS_ROW_PATCH_BOUNDS.edits);

type Field = CmsFieldMeta & { fields?: CmsFieldMeta[] };
const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === "object" && !Array.isArray(v);
const fail = (message: string): never => {
  throw new BadRequestError(message);
};
const REMOVED = Symbol("removed");

function patchObjectNode(
  children: CmsFieldMeta[],
  value: unknown,
  path: readonly (string | number)[],
  edit: CmsRowPatchEdit,
): Record<string, unknown> {
  const key = path[0];
  if (typeof key !== "string") return fail("CMS patch path expects a field identifier here");
  const child = children.find((c) => c.identifier === key);
  if (!child) return fail(`CMS patch path field not found: ${key}`);
  const obj: Record<string, unknown> =
    isPlainObject(value) ? { ...value } : value == null ? {} : fail("CMS patch path crosses a value of the wrong shape");
  const next = patchNode(child as Field, obj[key], path.slice(1), edit);
  if (next === REMOVED) delete obj[key];
  else obj[key] = next;
  return obj;
}

function patchNode(
  field: Field,
  value: unknown,
  path: readonly (string | number)[],
  edit: CmsRowPatchEdit,
): unknown {
  if (path.length === 0) return edit.remove ? REMOVED : edit.value;
  if (field.type === "list") {
    const index = path[0];
    if (typeof index !== "number") return fail("CMS patch path expects a list item index here");
    const items: unknown[] =
      Array.isArray(value) ? [...value] : value == null ? [] : fail("CMS patch path crosses a value of the wrong shape");
    if (index > items.length) return fail("CMS patch list index is beyond the end of the list");
    if (path.length === 1) {
      if (edit.remove) {
        if (index >= items.length) return fail("CMS patch list index does not exist");
        items.splice(index, 1);
      } else items[index] = edit.value;
      return items;
    }
    const item = index < items.length ? items[index] : {};
    items[index] = patchObjectNode(field.fields ?? [], item, path.slice(1), edit);
    return items;
  }
  if (field.type === "object") return patchObjectNode(field.fields ?? [], value, path, edit);
  return fail("CMS patch path descends into a field that is not a list or object");
}

export type CmsRowPatchTouched = {
  locale: string;
  name: string;
  field: CmsFieldMeta;
  /** Resulting root value (request-side media ids, if any, still present). */
  value: unknown;
};

/**
 * Applies the edits to a deep copy of the row's current draft base and
 * validates every touched root value against its native field (type, bounds,
 * nested required rule). Hidden and unique fields are outside the draft writer's
 * scope and stay outside here.
 */
export function applyCmsRowPatch(
  base: Record<string, Record<string, unknown>> | null,
  fields: readonly CmsFieldMeta[],
  locales: ReadonlySet<string>,
  rawEdits: readonly CmsRowPatchEdit[],
): { data: Record<string, Record<string, unknown>>; touched: CmsRowPatchTouched[] } {
  const edits = cmsRowPatchEditsSchema.parse(rawEdits);
  const data: Record<string, Record<string, unknown>> = base
    ? JSON.parse(JSON.stringify(base))
    : { "": {} };
  const touched = new Map<string, CmsRowPatchTouched>();
  for (const edit of edits) {
    if (!locales.has(edit.locale)) fail("CMS draft locale is not configured");
    const field = fields.find((f) => f.identifier === edit.field);
    if (
      !field ||
      !cmsNestedFieldSupported(field) ||
      field.hidden ||
      field.unique ||
      (edit.locale !== "" && !field.localized)
    )
      fail("CMS field is outside supported list/object/scalar draft scope");
    const perLocale = (data[edit.locale] ??= {});
    const result = patchNode(
      field as Field,
      perLocale[edit.field],
      edit.path ?? [],
      edit,
    );
    perLocale[edit.field] = result === REMOVED ? null : result;
    touched.set(JSON.stringify([edit.locale, edit.field]), {
      locale: edit.locale,
      name: edit.field,
      field: field!,
      value: perLocale[edit.field],
    });
  }
  for (const t of touched.values()) {
    t.value = data[t.locale][t.name];
    if (
      !cmsDraftValueMatches(t.field, t.value, { allowMediaId: true }) ||
      !cmsDraftFieldValueBounded(t.value)
    )
      fail("CMS patched value does not match the native field type or bounds");
  }
  return { data, touched: [...touched.values()] };
}
